import { useState, type MouseEvent, type JSX } from 'react'
import { Link as RouterLink, NavLink, Outlet, useLocation } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Toolbar from '@mui/material/Toolbar'
import useMediaQuery from '@mui/material/useMediaQuery'
import { BrandLockup, PRODUCT_NAME } from './Brand'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import MenuIcon from '@mui/icons-material/Menu'
import TokenOutlinedIcon from '@mui/icons-material/TokenOutlined'
import { useTheme } from '@mui/material/styles'
import { useSignOut } from '../auth'
import { formatNumber } from '../format'
import { useMe } from '../queries'

interface NavItem {
  to: string
  label: string
  adminOnly?: boolean
}

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard' },
  { to: '/jobs', label: 'Jobs' },
  { to: '/companies', label: 'Companies' },
  { to: '/contacts', label: 'Contacts' },
  // Review is the one part of this product that REQUIRES a person, and it lived
  // three clicks deep inside a job. Four hundred contacts sat undecided because
  // nobody who had not been told where to look would ever have found it.
  { to: '/reviews', label: 'Review' },
  // The other queue that needs a person: companies the repair ladder could not
  // settle on its own. Next to Review because it is the same kind of work.
  { to: '/repairs', label: 'Repairs' },
  // After Review because that is the order the work happens in: profile, review,
  // send, then find out whether the reviewing was any good.
  { to: '/outreach', label: 'Outreach' },
  { to: '/audit', label: 'Audit', adminOnly: true },
  // Beside Audit, because the two answer the same question from either end:
  // who can act here, and what they did.
  { to: '/users', label: 'Users', adminOnly: true },
  { to: '/settings', label: 'Settings', adminOnly: true },
]

function isActive(pathname: string, to: string): boolean {
  // The dashboard is the only exact match; everything else owns its subtree, so
  // a job detail page keeps "Jobs" lit rather than lighting nothing.
  return to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`)
}

export function AppLayout(): JSX.Element {
  const { pathname } = useLocation()
  const me = useMe()
  const signOut = useSignOut()
  const theme = useTheme()
  const narrow = useMediaQuery(theme.breakpoints.down('sm'))
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [navOpen, setNavOpen] = useState(false)

  const items = NAV.filter((item) => !item.adminOnly || me.data?.isAdmin)

  const openMenu = (event: MouseEvent<HTMLElement>): void => setAnchor(event.currentTarget)
  const closeMenu = (): void => setAnchor(null)

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="sticky">
        <Toolbar sx={{ gap: { xs: 1, lg: 3 }, minHeight: 56 }}>
          {/*
            Seven nav buttons, the brand, the credits chip and the account button
            need about 1,050px between them, and a Toolbar row cannot wrap. Below
            that the nav moves into a drawer rather than overflowing: it used to
            do neither, so every page under roughly 1,000px grew a horizontal
            scrollbar and pushed the credits chip and the account button off the
            right-hand edge until you scrolled sideways to find them.
          */}
          <IconButton
            color="inherit"
            aria-label="Open navigation"
            onClick={() => setNavOpen(true)}
            sx={{ display: { xs: 'inline-flex', lg: 'none' }, ml: -1 }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            component={RouterLink}
            to="/"
            aria-label={`${PRODUCT_NAME}, home`}
            sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center', flexShrink: 0 }}
          >
            <BrandLockup size={30} compact={narrow} />
          </Box>

          <Stack direction="row" spacing={0.5} sx={{ minWidth: 0, display: { xs: 'none', lg: 'flex' } }}>
            {items.map((item) => (
              <Button
                key={item.to}
                component={NavLink}
                to={item.to}
                size="small"
                color={isActive(pathname, item.to) ? 'primary' : 'inherit'}
                sx={{
                  fontWeight: isActive(pathname, item.to) ? 600 : 500,
                  borderBottom: 2,
                  borderRadius: 0,
                  borderColor: isActive(pathname, item.to) ? 'primary.main' : 'transparent',
                }}
              >
                {item.label}
              </Button>
            ))}
          </Stack>

          {/* Holds the right-hand end in place whether the nav row is shown or
              collapsed into the drawer. */}
          <Box sx={{ flexGrow: 1 }} />

          {/*
              Named after whichever provider the balance belongs to. The label
              was hard-coded to Enrich Layer while the figure was always the
              ACTIVE provider's, which was harmless only for as long as the
              other provider reported no balance at all and the chip stayed
              hidden. It reports one now, so the chip would have shown one
              account's credits under the other's name.
            */}
          {typeof me.data?.creditBalance === 'number' ? (
            <Tooltip
              title={`${
                me.data.providers?.find((p) => p.id === me.data?.activeProvider)?.label ?? 'Active provider'
              } credit balance`}
            >
              <Chip
                size="small"
                icon={<TokenOutlinedIcon />}
                label={`${formatNumber(me.data.creditBalance)} credits`}
                variant="outlined"
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  display: { xs: 'none', sm: 'inline-flex' },
                }}
              />
            </Tooltip>
          ) : null}

          <Button
            size="small"
            color="inherit"
            onClick={openMenu}
            startIcon={<AccountCircleOutlinedIcon />}
            sx={{ textTransform: 'none' }}
          >
            {me.data?.name || me.data?.email || 'Account'}
          </Button>
          <Menu anchorEl={anchor} open={anchor !== null} onClose={closeMenu}>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {me.data?.name ?? 'Signed in'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {me.data?.email ?? ''}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={() => {
                closeMenu()
                void signOut()
              }}
            >
              Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer open={navOpen} onClose={() => setNavOpen(false)}>
        <Box sx={{ width: 240 }} role="presentation" onClick={() => setNavOpen(false)}>
          <List>
            {items.map((item) => (
              <ListItemButton
                key={item.to}
                component={NavLink}
                to={item.to}
                selected={isActive(pathname, item.to)}
              >
                <ListItemText
                  primary={item.label}
                  slotProps={{ primary: { fontWeight: isActive(pathname, item.to) ? 600 : 500 } }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>

      <Container maxWidth={false} sx={{ py: 3, maxWidth: 1600 }}>
        {me.data && !me.data.enrichLayerConfigured ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No Enrich Layer API key is configured, so no job can spend credits. An admin sets it in Settings.
          </Alert>
        ) : null}
        <Outlet />
      </Container>
    </Box>
  )
}
